/**
 * ShieldWall — Cryptographic Primitives
 *
 * All crypto operations use the Web Crypto API (SubtleCrypto)
 * for hardware-accelerated performance via AES-NI instructions.
 *
 * @module crypto
 */

const encoder = new TextEncoder();

/**
 * SHA-256 hash using Web Crypto API (hardware-accelerated).
 * 10-50x faster than pure JS implementations.
 */
export async function sha256(message: string): Promise<string> {
  const buffer = await crypto.subtle.digest("SHA-256", encoder.encode(message));
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * HMAC-SHA256 signature using Web Crypto API.
 * Used for challenge-response integrity verification.
 */
export async function hmacSign(
  key: string,
  message: string
): Promise<string> {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    encoder.encode(key),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const signature = await crypto.subtle.sign(
    "HMAC",
    cryptoKey,
    encoder.encode(message)
  );
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * HMAC-SHA256 verification using Web Crypto API.
 * Constant-time comparison to prevent timing attacks.
 */
export async function hmacVerify(
  key: string,
  message: string,
  expectedSignature: string
): Promise<boolean> {
  const computed = await hmacSign(key, message);
  if (computed.length !== expectedSignature.length) return false;

  // Constant-time comparison
  let mismatch = 0;
  for (let i = 0; i < computed.length; i++) {
    mismatch |= computed.charCodeAt(i) ^ expectedSignature.charCodeAt(i);
  }
  return mismatch === 0;
}

/**
 * Generate a cryptographically secure random nonce.
 */
export function generateNonce(bytes: number = 16): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  const array = new Uint8Array(bytes);
  crypto.getRandomValues(array);
  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
